/**
 * /pqrsf — Bandeja del canal de servicio PQRSF.
 *
 * Requisito num. 10 del art. 1.5.1.8.1.1 de la Res. DIAN 000227 de 2025:
 * el canal debe garantizar trazabilidad y consulta, y estar "dispuesto
 * para la revisión de las funcionalidades y contenidos" por la DIAN. Esta
 * bandeja es la cara operativa de ese canal: filtros por estado y tipo,
 * indicador de vencidos y acceso al detalle con la línea de tiempo.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  Inbox,
  Loader2,
  Search,
  TimerOff,
} from 'lucide-react'

import { usePqrsfList, usePqrsfStats } from '@/hooks/api/use-pqrsf'
import {
  PQRSF_KIND_LABELS,
  PQRSF_STATUS_LABELS,
  type PqrsfFilter,
  type PqrsfKind,
  type PqrsfStatus,
  type PqrsfTicket,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'

const PAGE_SIZE = 25

/** Clases del badge por estado. Tokens semánticos, cero hex (regla §2.6). */
const STATUS_BADGE: Record<PqrsfStatus, string> = {
  radicado: 'bg-warning-tint text-warning-fg',
  en_tramite: 'bg-brand-50 text-brand-700',
  resuelto: 'bg-success-tint text-success-fg',
  cerrado: 'bg-bg-elevated text-fg-secondary',
}

export function PqrsfPage() {
  const [status, setStatus] = useState<PqrsfStatus | ''>('')
  const [kind, setKind] = useState<PqrsfKind | ''>('')
  const [search, setSearch] = useState('')
  const [overdue, setOverdue] = useState(false)
  const [page, setPage] = useState(0)

  const filter: PqrsfFilter = useMemo(
    () => ({
      status: status || undefined,
      kind: kind || undefined,
      search: search.trim() || undefined,
      overdue,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [status, kind, search, overdue, page],
  )

  const list = usePqrsfList(filter)
  const stats = usePqrsfStats()

  const totalPages = list.data ? Math.ceil(list.data.total / PAGE_SIZE) : 0

  /** Cualquier cambio de filtro vuelve a la primera página. */
  const resetPage = () => setPage(0)

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8">
        <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">
          ▸ Servicio al cliente
        </p>
        <h1 className="mt-1 text-3xl font-bold text-fg-primary">PQRSF</h1>
        <p className="mt-2 max-w-3xl text-sm text-fg-secondary">
          Peticiones, quejas, reclamos, sugerencias y felicitaciones radicadas por el
          canal público. Cada cambio queda registrado en una trazabilidad inmutable
          (Res. DIAN 227/2025, art. 1.5.1.8.1.1 num. 10).
        </p>
      </div>

      {/* Resumen */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Total" value={stats.data?.total} />
        <StatCard label="Radicados" value={stats.data?.by_status?.radicado} />
        <StatCard label="En trámite" value={stats.data?.by_status?.en_tramite} />
        <StatCard label="Resueltos" value={stats.data?.by_status?.resuelto} />
        <StatCard label="Vencidos" value={stats.data?.overdue} emphasis={Boolean(stats.data?.overdue)} />
      </div>

      {/* Filtros */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-border bg-bg-surface p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <label htmlFor="pqrsf-status" className="text-sm font-medium text-fg-secondary">
            Estado:
          </label>
          <select
            id="pqrsf-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as PqrsfStatus | '')
              resetPage()
            }}
            className="rounded-md border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary"
          >
            <option value="">Todos</option>
            {Object.entries(PQRSF_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="pqrsf-kind" className="text-sm font-medium text-fg-secondary">
            Tipo:
          </label>
          <select
            id="pqrsf-kind"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as PqrsfKind | '')
              resetPage()
            }}
            className="rounded-md border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary"
          >
            <option value="">Todos</option>
            {Object.entries(PQRSF_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-fg-muted" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              resetPage()
            }}
            placeholder="Radicado, asunto o solicitante"
            aria-label="Buscar por radicado, asunto o solicitante"
            className="w-64 rounded-md border border-border bg-bg-surface px-3 py-1.5 text-sm text-fg-primary"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-fg-secondary">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked)
              resetPage()
            }}
          />
          <TimerOff className="h-4 w-4" aria-hidden />
          Sólo vencidos
        </label>

        {list.data && (
          <div className="ml-auto flex items-center gap-2 text-sm text-fg-muted">
            {list.isFetching && (
              <span className="inline-flex items-center gap-1 text-xs">
                <Loader2 className="h-3 w-3 animate-spin" />
                Actualizando…
              </span>
            )}
            {list.data.total.toLocaleString('es-CO')} resultados
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-hidden rounded-xl border border-border bg-bg-surface shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-elevated">
              <tr>
                <Th>Radicado</Th>
                <Th>Estado</Th>
                <Th>Tipo</Th>
                <Th>Asunto</Th>
                <Th>Solicitante</Th>
                <Th>Radicado el</Th>
                <Th>Vence</Th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-fg-muted">
                    Cargando…
                  </td>
                </tr>
              )}
              {!list.isLoading && list.data?.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-fg-muted">
                    <Inbox className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    Sin solicitudes para los filtros aplicados.
                  </td>
                </tr>
              )}
              {list.data?.items.map((row) => (
                <Row key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>

        {list.data && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <p className="text-xs text-fg-muted">
              Página {page + 1} de {totalPages}
            </p>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || list.isFetching}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ChevronLeft className="h-3 w-3" />
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages || list.isFetching}
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary disabled:cursor-not-allowed disabled:opacity-60"
              >
                Siguiente
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  emphasis = false,
}: {
  label: string
  value: number | undefined
  emphasis?: boolean
}) {
  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${
        emphasis ? 'border-error bg-error-tint' : 'border-border bg-bg-surface'
      }`}
    >
      <p className="font-mono text-xs uppercase tracking-wider text-fg-muted">{label}</p>
      <p
        className={`mt-1 text-2xl font-bold ${
          emphasis ? 'text-error-fg' : 'text-fg-primary'
        }`}
      >
        {value === undefined ? '—' : value.toLocaleString('es-CO')}
      </p>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-3 text-left font-semibold text-fg-secondary">{children}</th>
}

function Row({ row }: { row: PqrsfTicket }) {
  const overdue =
    Boolean(row.due_at) &&
    new Date(row.due_at as string) < new Date() &&
    (row.status === 'radicado' || row.status === 'en_tramite')

  return (
    <tr className="border-t border-border hover:bg-bg-elevated">
      <td className="px-4 py-3">
        <Link
          to={ROUTES.PQRSF_DETAIL.replace(':ticketId', row.id)}
          className="font-mono text-xs font-semibold text-brand-700 hover:underline"
        >
          {row.radicado}
        </Link>
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_BADGE[row.status]
          }`}
        >
          {PQRSF_STATUS_LABELS[row.status]}
        </span>
      </td>
      <td className="px-4 py-3 text-fg-secondary">{PQRSF_KIND_LABELS[row.kind]}</td>
      <td className="max-w-xs px-4 py-3 text-fg-primary">
        <div className="truncate" title={row.subject}>
          {row.subject}
        </div>
      </td>
      <td className="px-4 py-3 text-fg-secondary">{row.requester_name}</td>
      <td className="px-4 py-3 text-fg-secondary">{formatDate(row.filed_at)}</td>
      <td className={`px-4 py-3 ${overdue ? 'font-medium text-error-fg' : 'text-fg-secondary'}`}>
        {row.due_at ? formatDate(row.due_at) : '—'}
      </td>
    </tr>
  )
}

/** Fecha corta es-CO. Exportada para reusar en el detalle. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
